import * as React from 'react'
import { cn } from '../../lib/cn'
import { ForkConnector } from './ForkConnector'
import { LaneCard } from './LaneCard'
import { SpineTrack } from './SpineTrack'
import type { BranchedLaneRowData } from './types'

export interface BranchedLaneRowProps extends React.HTMLAttributes<HTMLDivElement> {
  /**
   * Two parallel rows. The first renders on top, the second below.
   * Mockup puts 02A (existing-user) on top, 02B (new-user) on bottom.
   */
  rows: [BranchedLaneRowData, BranchedLaneRowData]
  /**
   * When true (default) the row group includes the fork-out connector
   * on the left and fork-in connector on the right. Set false when the
   * parent layout wants to control connector placement itself.
   */
  withForkConnectors?: boolean
  /** Override fork-out top/bottom stroke colors. */
  forkOutTopColor?: string
  forkOutBottomColor?: string
  /** Override fork-in stroke colors. Defaults track the lane accents. */
  forkInTopColor?: string
  forkInBottomColor?: string
  /** Joint dot color overrides. */
  forkOutJointColor?: string
  forkInJointColor?: string
}

/**
 * Branched lane area — two parallel rows, each a lane card on the
 * left and a spine track on the right. Fork-out connector fans in
 * from the left; fork-in connector converges out on the right.
 *
 *   [fork-out] | [lane 02A | spine 02A]
 *              | [lane 02B | spine 02B]   | [fork-in]
 *
 * Source of truth: `mockups/unified-pipeline.html` `.col-branchspine`.
 */
export const BranchedLaneRow = React.forwardRef<HTMLDivElement, BranchedLaneRowProps>(
  function BranchedLaneRow(
    {
      className,
      rows,
      withForkConnectors = true,
      forkOutTopColor,
      forkOutBottomColor,
      forkInTopColor,
      forkInBottomColor,
      forkOutJointColor,
      forkInJointColor,
      style,
      ...rest
    },
    ref,
  ) {
    const [top, bottom] = rows

    return (
      <div
        ref={ref}
        className={cn('flex h-full items-stretch', className)}
        style={style}
        data-testid="pipeline-branched-lane-row"
        {...rest}
      >
        {withForkConnectors && (
          <ForkConnector
            direction="out"
            topColor={forkOutTopColor ?? top.lane.accentColor}
            bottomColor={forkOutBottomColor ?? bottom.lane.accentColor}
            jointColor={forkOutJointColor}
          />
        )}

        <div
          className="flex h-full flex-col gap-[18px]"
          // Width = lane (280) + gap (24) + spine min-room (~580). Set as
          // min-width so the column never collapses below this when the
          // canvas is scrolled.
          style={{ minWidth: 280 + 24 + 580 }}
        >
          <div className="flex min-h-0 flex-1 items-stretch" data-testid="pipeline-lane-row">
            <LaneCard lane={top.lane} />
            <SpineTrack chips={top.spineChips} leaderStyle={top.leaderStyle ?? 'dotted'} />
          </div>
          <div className="flex min-h-0 flex-1 items-stretch" data-testid="pipeline-lane-row">
            <LaneCard lane={bottom.lane} />
            <SpineTrack chips={bottom.spineChips} leaderStyle={bottom.leaderStyle ?? 'solid'} />
          </div>
        </div>

        {withForkConnectors && (
          <ForkConnector
            direction="in"
            topColor={forkInTopColor ?? top.lane.accentColor}
            bottomColor={forkInBottomColor ?? bottom.lane.accentColor}
            jointColor={forkInJointColor}
          />
        )}
      </div>
    )
  },
)
